<template>
  <div class="field no-select">
    <label
      class="mb-2 label no-select"
      :for="name"
    >
      <h3 class="p-600">
        {{ label }}
      </h3>
    </label>
    <br />
    <p
      v-if="hasHelpText"
      class="help-text"
      v-html="finalHelpText"
    ></p>
    <input
      :id="name"
      ref="input"
      type="text"
      :placeholder="placeholder"
      :disabled="isDisabled"
      :value="inputValue"
      @change="setModel"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import Tagify from '@yaireo/tagify';
import isObject from '@/helpers/isObject';
import uniqueId from '@/helpers/uniqueId';
import commonProperties from './common-properties';

export default {
  name: 'Tags',
  mixins: [commonProperties],
  inheritAttrs: false,
  props: {
    pattern: RegExp,
    whitelistFromCustomerSetting: {
      type: String,
      default: undefined,
    },
    moved: {
      type: Boolean,
      default: undefined,
    },
    whitelistFromPages: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    libOptions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      tagger: null,
    };
  },
  computed: {
    pages() {
      return this.pagesArray(this.container.language);
    },
    inputValue() {
      if (typeof this.model === 'undefined') {
        return '';
      }

      if (isObject(this.model)) {
        return JSON.stringify(
          Object.entries(this.model).map(([key, value]) => ({ value, key })),
        );
      }

      if (this.whitelist) {
        return JSON.stringify(
          this.model
            .filter((tag) => {
              if (this.whitelistFromPages) {
                return this.pages.find((page) => page.id === tag);
              }
              return (
                Object.prototype.hasOwnProperty.call(
                  this.whitelistSource,
                  tag,
                ) || tag
              );
            })
            .map((tag) => {
              if (this.whitelistFromPages) {
                const matchedPage = this.pages.find((page) => page.id === tag);
                return {
                  value: tag,
                  label: matchedPage.title,
                  isPublished: matchedPage.isPublished,
                };
              }
              return {
                value: tag,
                label: this.whitelistSource[tag],
              };
            }),
        );
      }

      return JSON.stringify(this.model.map((value) => ({ value })));
    },
    whitelist() {
      if (this.whitelistFromPages) {
        return this.pages.map((page) => ({
          label: page.title,
          value: page.id,
          isPublished: page.isPublished,
        }));
      }
      if (typeof this.whitelistSource === 'object') {
        return Object.values(this.whitelistSource).map((value) => ({
          label: value,
          value,
        }));
      }

      return null;
    },
    whitelistSource() {
      return this.customerSettings[this.whitelistFromCustomerSetting];
    },
    ...mapState(['customerSettings']),
    ...mapGetters(['container', 'pagesArray']),
  },
  watch: {
    moved() {
      this.$nextTick(() => {
        this.tagger.destroy();
        this.startTagify();
      });
    },
    whitelist() {
      this.$nextTick(() => {
        this.tagger.destroy();
        this.startTagify();
      });
    },
  },
  mounted() {
    this.startTagify();
  },
  methods: {
    setModel() {
      this.model = isObject(this.model)
        ? this.tagger.value.reduce(
            (acc, { key = uniqueId(), value }) => ({ ...acc, [key]: value }),
            {},
          )
        : this.tagger.value.map((tag) => tag.value);
    },
    startTagify() {
      const options = {
        pattern: this.pattern,
        tagTextProp: 'label',
        transformTag: (newTag, oldTag = { key: uniqueId() }) => {
          if (!newTag.key) {
            // eslint-disable-next-line no-param-reassign
            newTag.key = oldTag.key;
          }
        },
        ...this.libOptions,
      };

      if (this.whitelist) {
        options.whitelist = this.whitelist;
        options.dropdown = {
          enabled: 0,
          mapValueTo: 'label',
          searchKeys: ['label'],
        };
      }

      if (this.whitelistFromPages) {
        options.enforceWhitelist = true;
        options.templates = {
          tag(tagData) {
            return `<tag
                      title="${tagData.label}"
                      contenteditable='false'
                      spellcheck='false'
                      tabIndex="0"
                      class="tagify__tag ${tagData.class ? tagData.class : ''}"
                      ${this.getAttributes(tagData)}
                    >
                      <x title="Remover" class="tagify__tag__removeBtn"></x>
                      <div>
                          <span class="tagify__tag-text">${tagData.label}</span>
                          <span
                            class="status-circle status-circle--${
                              tagData.isPublished ? 'published' : 'draft'
                            }"
                            title="${
                              tagData.isPublished ? 'Publicado' : 'Rascunho'
                            }"
                          >
                          </span>
                      </div>
                    </tag>`;
          },
          dropdownItem(item) {
            return `<div
                      class="${this.settings.classNames.dropdownItem}"
                      tabindex="0"
                      role="option"
                      ${this.getAttributes(item)}
                    >
                      ${item.label}
                      <span
                        class="status-circle status-circle--${
                          item.isPublished ? 'published' : 'draft'
                        }"
                        title="${item.isPublished ? 'Publicado' : 'Rascunho'}"
                      >
                      </span>
                    </div>`;
          },
        };
      }

      this.tagger = new Tagify(this.$refs.input, options);
    },
  },
};
</script>

<style lang="scss">
$tags-border-color: #e8e8e8;
$tags-focus-border-color: $crm-blue-primary;
$tag-hover: lighten($tags-focus-border-color, 27%);
$tagify-dd-color-primary: $crm-blue-primary;

@import '@yaireo/tagify/src/tagify.scss';

.tagify {
  width: 100%;
  background-color: $lighter;
  border: 1px solid $light;
  border-radius: 4px;
  padding: 2px 8px;

  &__input {
    font-size: $size-small;
    color: $darker;
  }

  &--focus {
    border-color: $crm-blue-primary;
  }
}

.status-circle {
  display: inline-block;
  height: 8px;
  width: 8px;
  margin-left: 8px;
  vertical-align: middle !important;
  border-radius: 50%;

  &--published {
    background-color: $success;
  }

  &--draft {
    background-color: $gray-70;
  }
}
</style>

<style scoped lang="scss">
h3 {
  font-weight: $lb-medium;
  font-size: $size-normal;
  line-height: 1.5;
  color: $dark;
}
</style>
